import { motion } from "framer-motion";
import { MapPin, Navigation } from "lucide-react";
import { ChapterHeading } from "@/components/ChapterHeading";

export const LocationChapter = () => (
  <section className="px-6 py-24 md:px-16 md:py-36 lg:px-24" data-testid="chapter-location">
    <ChapterHeading number="02" title="The" italic="Location" testId="chapter-location-heading" />
    <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden border border-white/10 md:col-span-7 md:row-span-2"
        data-testid="location-image-card"
      >
        <img
          src="/venue-60.png"
          alt="Giant illuminated 60 surrounded by black and gold balloons at the venue"
          className="h-full max-h-[80vh] min-h-[420px] w-full object-cover transition-transform duration-700 hover:scale-105"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-8 py-6 backdrop-blur-sm">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">The Venue</p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ y: -4 }}
        className="border border-white/10 bg-[#0D0D0D] p-10 transition-colors duration-300 hover:border-gold/30 md:col-span-5 md:p-12"
        data-testid="location-address-card"
      >
        <MapPin className="mb-8 h-5 w-5 text-gold" strokeWidth={1.25} />
        <p className="font-display text-3xl font-light text-[#F5F5F0] md:text-4xl">Westcliff Rugby Club</p>
        <p className="mt-4 text-sm leading-relaxed text-[#A1A19A]">
          The Gables, Cherry Orchard Way
          <br />
          Rochford, Essex, SS4 1YG
          <br />
          Head for The Gables clubhouse once you're into the ground.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ y: -4 }}
        className="border border-white/10 bg-[#0D0D0D] p-10 transition-colors duration-300 hover:border-gold/30 md:col-span-5 md:p-12"
        data-testid="location-directions-card"
      >
        <Navigation className="mb-8 h-5 w-5 text-gold" strokeWidth={1.25} />
        <p className="font-display text-2xl font-light text-[#F5F5F0] md:text-3xl">Getting there</p>
        <p className="mt-4 text-sm leading-relaxed text-[#A1A19A]">
          Coming by train? Rochford and Southend Airport are the nearest
          stations — allow around ten to twelve minutes in a taxi from either
          to the venue.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-[#A1A19A]">
          Driving? There is free parking at the Gables. Turn off Cherry Orchard
          Way onto Southend Airport Business Park. Go past Costa Coffee on your
          right and straight over the roundabout. A big building (The Event Co)
          is on your left. Turn left after that. Follow the road round and into
          the car park.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-[#A1A19A]">
          Come into the clubhouse and come up to the function room on the first
          floor — there is a lift for disabled access and those who struggle
          with stairs. The clubhouse bar will be pouring all evening.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-[#A1A19A]">
          Had a tipple or two? The club has CCTV covering the car park; cars can
          be safely left overnight if you want to get a taxi home.
        </p>
        <div className="mt-8" data-testid="location-taxi-list">
          <p className="mb-3 text-xs uppercase tracking-[0.25em] text-[#666666]">Local taxis</p>
          <ul className="space-y-2 text-sm text-[#A1A19A]">
            <li>
              AC Radio Cabs —{" "}
              <a data-testid="taxi-link-ac-radio-cabs" href="tel:+441702334455" className="border-b border-white/20 pb-0.5 transition-colors duration-200 hover:border-gold hover:text-gold">
                01702 33 44 55
              </a>
            </li>
            <li>
              Bestax —{" "}
              <a data-testid="taxi-link-bestax" href="tel:+441702667788" className="border-b border-white/20 pb-0.5 transition-colors duration-200 hover:border-gold hover:text-gold">
                01702 66 77 88
              </a>
            </li>
            <li>
              Southend Taxi Co-op —{" "}
              <a data-testid="taxi-link-southend-coop" href="tel:+441702333444" className="border-b border-white/20 pb-0.5 transition-colors duration-200 hover:border-gold hover:text-gold">
                01702 333 444
              </a>
            </li>
          </ul>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-6">
          <a
            data-testid="location-map-link"
            href="https://maps.google.com/?q=Westcliff+Rugby+Club,+Cherry+Orchard+Way,+Rochford,+SS4+1YG"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block border-b border-gold/50 pb-1 text-xs uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:border-gold hover:text-gold-hover"
          >
            Open in Maps
          </a>
          <a
            data-testid="location-website-link"
            href="https://www.westcliffrfc.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block border-b border-white/20 pb-1 text-xs uppercase tracking-[0.25em] text-[#A1A19A] transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            Club website
          </a>
          <a
            data-testid="location-phone-link"
            href="tel:+441702541499"
            className="inline-block border-b border-white/20 pb-1 text-xs uppercase tracking-[0.25em] text-[#A1A19A] transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            01702 541499
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);
